import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Row, Col, Card, Space, Divider, Tag, Collapse } from 'antd';
import {
  CheckCircleOutlined,
  StarFilled,
  UsergroupAddOutlined,
  DatabaseOutlined,
  RightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { PublicPageLayout } from '../../components/public/PublicPageLayout';
import { SEOHead } from '../../components/SEOHead';
import {
  PUBLIC_PLAN_ORDER,
  getSubscriptionPlanConfig,
  formatPlanTaxIncludedLabel,
} from '@shared/config/subscription-plans';
import '../../styles/new-service-plans.css';

const { Title, Paragraph, Text } = Typography;

// ヘッダーセクション
const PlansHeaderSection: React.FC = () => {
  return (
    <section className="plans-header-section">
      <div className="plans-header-bg">
        <img
          src="/images/landing/facility-blue.jpg"
          alt="設備管理"
          className="plans-header-bg-image"
        />
        <div className="plans-header-overlay"></div>
      </div>
      <div className="section-container">
        <div className="plans-header-content">
          <Title level={1} style={{ color: 'white', margin: 0 }}>
            料金プラン
          </Title>
          <Paragraph style={{ fontSize: 18, color: 'rgba(255,255,255,0.9)', marginTop: 16 }}>
            シンプルで分かりやすい料金体系。ユーザー数無制限、全プラン共通。
          </Paragraph>
        </div>
      </div>
    </section>
  );
};

// プランカード
const PlanCardSection: React.FC = () => {
  const plans = PUBLIC_PLAN_ORDER.map((code) => {
    const plan = getSubscriptionPlanConfig(code);
    return {
      code,
      ...plan,
      isPopular: code === 'standard',
    };
  });

  return (
    <section className="plan-cards-section">
      <div className="section-container">
        <Row gutter={[24, 32]} justify="center">
          {plans.map((plan) => (
            <Col xs={24} md={12} lg={plan.code === 'enterprise_premium' ? 24 : 8} key={plan.code}>
              <Card className={`plan-card ${plan.isPopular ? 'plan-card--popular' : ''} ${plan.code === 'enterprise_premium' ? 'plan-card--premium' : ''}`}>
                {plan.isPopular && (
                  <div className="plan-badge">
                    <StarFilled style={{ marginRight: 4 }} />
                    人気
                  </div>
                )}

                <div className="plan-card-header">
                  <Title level={3}>{plan.label}</Title>
                  <Paragraph className="plan-scene">{plan.scene}</Paragraph>

                  <div className="plan-price">
                    {plan.displayPriceOverride ? (
                      <div className="price-custom">{plan.displayPriceOverride}</div>
                    ) : (
                      <>
                        <span className="price-yen">¥</span>
                        <span className="price-amount">
                          {new Intl.NumberFormat('ja-JP').format(plan.monthlyPrice)}
                        </span>
                        <span className="price-period">/月</span>
                      </>
                    )}
                  </div>
                  {!plan.displayPriceOverride && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      ({formatPlanTaxIncludedLabel(plan)})
                    </Text>
                  )}
                </div>

                <Divider style={{ margin: '24px 0' }} />

                <div className="plan-card-body">
                  <div className="plan-spec">
                    <UsergroupAddOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                    <div>
                      <Text strong>ユーザー数</Text>
                      <div className="spec-value">
                        {plan.userLimit === null ? '無制限' : `${plan.userLimit}名まで`}
                      </div>
                    </div>
                  </div>

                  <div className="plan-spec">
                    <DatabaseOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                    <div>
                      <Text strong>設備登録数</Text>
                      <div className="spec-value">
                        {plan.equipmentLimit === null ? '無制限' : `${plan.equipmentLimit.toLocaleString()}台まで`}
                      </div>
                    </div>
                  </div>

                  <div className="plan-spec">
                    <DatabaseOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                    <div>
                      <Text strong>ストレージ容量</Text>
                      <div className="spec-value">
                        {plan.storageCapacityGB === null
                          ? '無制限'
                          : plan.storageCapacityGB >= 1024
                            ? `${plan.storageCapacityGB / 1024}TB`
                            : `${plan.storageCapacityGB}GB`}
                      </div>
                    </div>
                  </div>

                  <div className="plan-summary">
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {plan.shortDescription}
                    </Text>
                  </div>

                  <Collapse
                    ghost
                    size="small"
                    items={[
                      {
                        key: '1',
                        label: (
                          <Text strong>
                            <InfoCircleOutlined style={{ marginRight: 8 }} />
                            詳細を見る
                          </Text>
                        ),
                        children: (
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                                特徴
                              </Text>
                              <ul className="feature-list">
                                {plan.featureHighlights.map((highlight, idx) => (
                                  <li key={idx}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                                    <span>{highlight}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                                想定用途
                              </Text>
                              <ul className="use-case-list">
                                {plan.useCases.map((useCase, idx) => (
                                  <li key={idx}>
                                    <RightOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                                    <span>{useCase}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </div>

                <div className="plan-card-footer">
                  <Link
                    className={`public-btn ${plan.isPopular ? 'public-btn--primary' : ''} public-btn--block`}
                    to="/register"
                  >
                    このプランで始める
                  </Link>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// 追加オプションセクション
const AddonSection: React.FC = () => {
  return (
    <section className="addon-section">
      <div className="section-container-narrow">
        <div className="section-header">
          <Title level={2}>追加オプション（オプション追加）</Title>
          <Paragraph className="section-description">
            必要に応じて柔軟に拡張できます
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Card className="addon-card">
              <div className="addon-header">
                <DatabaseOutlined style={{ fontSize: 32, color: '#1677ff' }} />
                <Title level={4}>設備数追加</Title>
              </div>
              <div className="addon-price">
                <span className="addon-amount">¥20,000</span>
                <span className="addon-unit">/月・50台</span>
              </div>
              <Paragraph className="addon-description">
                設備登録数を50台単位で追加できます
              </Paragraph>
              <div className="addon-example">
                <Text type="secondary" style={{ fontSize: 13 }}>
                  例: スタンダード(300台) + 50台 = 350台対応<br />
                  月額69,800円
                </Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card className="addon-card">
              <div className="addon-header">
                <DatabaseOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                <Title level={4}>ストレージ容量追加</Title>
              </div>
              <div className="addon-price">
                <span className="addon-amount">¥5,000</span>
                <span className="addon-unit">/月・50GB</span>
              </div>
              <Paragraph className="addon-description">
                ストレージ容量を50GB単位で追加できます
              </Paragraph>
              <div className="addon-example">
                <Text type="secondary" style={{ fontSize: 13 }}>
                  例: スタンダード(100GB) + 50GB×4 = 300GB<br />
                  月額69,800円
                </Text>
              </div>
            </Card>
          </Col>
        </Row>

        <Card className="addon-note-card" style={{ marginTop: 24 }}>
          <Text>
            ※ 追加オプションはプランごとに上限があります。詳細はお問い合わせください。
          </Text>
        </Card>
      </div>
    </section>
  );
};

// プラン選択ガイド
const PlanGuideSection: React.FC = () => {
  const guides = [
    {
      title: 'スターター',
      tags: ['試用導入', '小規模'],
      points: [
        'まずは小規模で試したい',
        '重要設備だけ管理したい',
        '無料登録後の本格導入前評価',
      ],
    },
    {
      title: 'スタンダード',
      tags: ['小規模工場', '最も人気'],
      popular: true,
      points: [
        '小規模工場(設備300台程度)',
        'チーム全員で情報共有したい',
        'コストを抑えて本格運用したい',
      ],
    },
    {
      title: 'プロフェッショナル',
      tags: ['中規模工場', '複数部門'],
      points: [
        '中規模工場(設備1,000台程度)',
        '複数部門で横断的に管理',
        '高度な設備分析が必要',
      ],
    },
    {
      title: 'エンタープライズ',
      tags: ['大規模工場', '複数拠点'],
      points: [
        '大規模工場(設備2,500台程度)',
        '複数拠点の統合管理',
        '高度な分析とレポートが必要',
      ],
    },
  ];

  return (
    <section className="plan-guide-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>プラン選択ガイド</Title>
          <Paragraph className="section-description">
            あなたに最適なプランを見つけましょう
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {guides.map((guide, index) => (
            <Col xs={24} md={12} lg={6} key={index}>
              <Card className={`guide-card ${guide.popular ? 'guide-card--popular' : ''}`}>
                <Title level={4}>{guide.title}</Title>
                <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
                  {guide.tags.map((tag, idx) => (
                    <Tag key={idx} color={guide.popular ? 'blue' : 'default'}>
                      {tag}
                    </Tag>
                  ))}
                </Space>
                <ul className="guide-points">
                  {guide.points.map((point, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined style={{ color: guide.popular ? '#1677ff' : '#52c41a' }} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// FAQ
const PlansFAQSection: React.FC = () => {
  const faqs = [
    {
      q: 'プランの変更はできますか?',
      a: 'はい、いつでもプランのアップグレード・ダウングレードが可能です。変更は翌月から適用されます。',
    },
    {
      q: '追加オプションは途中で解約できますか?',
      a: 'はい、可能です。ただし、追加オプション解約後の上限内に設備数や予測枠が収まっている必要があります。',
    },
    {
      q: '無料登録はありますか?',
      a: 'はい、無料登録プランをご用意しています。クレジットカード登録不要で全機能をお試しいただけます。',
    },
    {
      q: '支払い方法は?',
      a: 'クレジットカード決済に対応しています。',
    },
  ];

  return (
    <section id="faq" className="plans-faq-section">
      <div className="section-container-narrow">
        <div className="section-header">
          <Title level={2}>よくある質問</Title>
        </div>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {faqs.map((faq, index) => (
            <Card key={index} className="faq-card">
              <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                Q. {faq.q}
              </Text>
              <Text style={{ color: '#595959' }}>
                A. {faq.a}
              </Text>
            </Card>
          ))}
        </Space>
      </div>
    </section>
  );
};

// CTA
const PlansCTASection: React.FC = () => {
  return (
    <section className="plans-cta-section">
      <div className="section-container-narrow">
        <Card className="plans-cta-card">
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <Title level={2} style={{ margin: 0, color: 'white' }}>
              無料登録実施中
            </Title>
            <Paragraph style={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }}>
              クレジットカード登録不要。全機能をお試しいただけます。
            </Paragraph>
            <Link
              className="public-btn public-btn--primary public-btn--lg"
              to="/register"
            >
              今すぐ無料登録を開始
            </Link>
            <Space direction="vertical" size="small">
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
                プランの詳細やご不明点がある場合は
              </Text>
              <Link to="/contact" style={{ color: 'white', fontSize: 16, fontWeight: 500, textDecoration: 'underline' }}>
                お問い合わせください
              </Link>
            </Space>
          </Space>
        </Card>
      </div>
    </section>
  );
};

// メインコンポーネント
export const ServicePlansPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="ご利用料金・プラン｜一元帳"
        description="一元帳の料金プランをご紹介。設備・部品・業者・作業履歴を一元管理できる設備管理ソフトを、利用規模や用途に合わせて選べます。"
      />
      <PublicPageLayout>
        <div className="new-service-plans-page">
        <PlansHeaderSection />
        <PlanCardSection />
        <AddonSection />
        <PlanGuideSection />
        <PlansFAQSection />
        <PlansCTASection />
      </div>
    </PublicPageLayout>
    </>
  );
};
